import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { NgSelectOption } from "@angular/forms";
import { NgbTime } from "@ng-bootstrap/ng-bootstrap/timepicker/ngb-time";
import { NgSelectComponent } from "@ng-select/ng-select";
import { DropdownItem } from "models/order-management/dropdownItem";
import { MAREditedAction } from "models/order-management/MAREditedAction";
import { Action, Order } from "models/order-management/models";
import { OrderComponent } from "models/order-management/orderComponent";
import { OrderComponentPropertyConfiguration } from "models/order-management/orderComponentPropertyConfiguration";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { Subscription } from "rxjs";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "mar-details",
  templateUrl: "mar-details.html",
})
export class MARDetailsComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    if (
      this._orderFormState &&
      this._orderFormState.MAR === orderFormState.MAR
    ) {
      return;
    }
    this._orderFormState = orderFormState;
    // Clear details when MAR checkbox de-selected
    if (
      this._orderFormState.MAR === true &&
      !this.orderCreateRequest.postActions
    ) {
      this.orderCreateRequestEvent.emit({
        updates: {
          postActions: [this.MAREditedAction],
        },
      });
    } else if (this._orderFormState.MAR === false) {
      this.clearMARDetails(true);
    }
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();

  /****************************************************************************
   * ViewChild                                                                *
   ****************************************************************************/
  @ViewChild("daysOfTheWeekDropdownId")
  daysOfTheWeekDropdown: NgSelectComponent;
  @ViewChild("timesDropdownId") timesDropdown: NgSelectComponent;
  @ViewChild("frequencyDropdownId") frequencyDropdown: NgSelectComponent;

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  // Internal State
  _orderFormState: OrderFormState;
  MARDetails = false;
  time: NgbTime;
  areSelectedTimesOrSelectedDaysOfTheWeekPopulated: boolean = false;
  isAdministrationFrequencyPopulated: boolean = false;

  // Fields
  selectTimesSet: Set<string> = new Set();
  selectedTimes: string[] = [];
  MARNotes = "";
  selectedDaysOfTheWeek: string[] = [];
  selectedAdministrationFrequency: string = "";
  dayOfTheWeekSortValueByDay: { [key: string]: number } = {};
  MAREditedAction: MAREditedAction = this.getBaseMAREditedAction();
  administrationFrequency: string;

  // Components
  timesComponent: OrderComponentPropertyConfiguration;
  daysOfTheWeekComponent: OrderComponentPropertyConfiguration;
  notesComponent: OrderComponentPropertyConfiguration;
  frequencyComponent: OrderComponentPropertyConfiguration;

  // Subscriptions
  updateSubscription?: Subscription;

  constructor() {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    [
      this.daysOfTheWeekComponent,
      this.frequencyComponent,
      this.notesComponent,
      this.timesComponent,
    ] = this.orderComponent.properties;
    // Given the configuration, assign each day of the week a sort value, e.g., { SUNDAY: 0, MONDAY: 1, etc. }
    this.dayOfTheWeekSortValueByDay =
      this.daysOfTheWeekComponent.dropdownItems!.reduce<{
        [key: string]: number;
      }>((acc, val, index) => {
        acc[val.value!] = index;
        return acc;
      }, {});
    // Any time the request is updated, check if valid
    this.updateSubscription = this.orderCreateRequestEvent.subscribe(() => {
      this.checkIsValid();
    });
    this.checkIsValid();
  }

  ngOnDestroy() {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  /****************************************************************************
   * MAR Details                                                              *
   ****************************************************************************/

  get shouldShow(): boolean {
    return !!this._orderFormState.MAR;
  }

  // A click to add or remove details
  toggleMARDetails() {
    if (this.MARDetails) {
      this.MARDetails = false;
      this.clearMARDetails();
    } else {
      this.MARDetails = true;
      this.checkIsValid();
    }
  }

  // Clear all MARDetail fields and empty request
  clearMARDetails(deleteAction = false) {
    this.MARDetails = false;
    this.time = undefined;
    this.selectTimesSet = new Set();
    this.selectedTimes = [];
    this.MARNotes = "";
    this.selectedDaysOfTheWeek = [];
    this.selectedAdministrationFrequency = undefined;
    this.MAREditedAction = this.getBaseMAREditedAction();
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
    this.setIsAdministrationFrequencyPopulated();
    if (
      this.timesComponent &&
      this.daysOfTheWeekComponent &&
      this.frequencyComponent &&
      this.notesComponent
    ) {
      if (deleteAction) {
        this.orderCreateRequestEvent.emit({
          deletes: ["postActions"],
        });
      } else {
        this.orderCreateRequestEvent.emit({
          updates: {
            postActions: [this.MAREditedAction],
          },
        });
      }
    }
  }

  checkIsValid() {
    if (!this.MARDetails) {
      this.isValidEvent.emit(true);
      return;
    }

    this.isValidEvent.emit(true);
    return;
  }

  setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated() {
    this.areSelectedTimesOrSelectedDaysOfTheWeekPopulated =
      this.selectedTimes.length > 0 || this.selectedDaysOfTheWeek.length > 0;
  }

  setIsAdministrationFrequencyPopulated() {
    this.isAdministrationFrequencyPopulated =
      this.administrationFrequency !== undefined;
  }

  /****************************************************************************
   * Times Components                                                         *
   ****************************************************************************/

  get canAddTime(): boolean {
    return this.time &&
      !(this.time.hour == undefined || this.time.minute == undefined)
      ? true
      : false;
  }

  // Pad times and convert to strings. Do not add duplicates.
  onAddTime() {
    const [hour, minute] = [this.time.hour, this.time.minute].map((time) =>
      `${time}`.padStart(2, "0")
    );
    this.selectTimesSet.add(`${hour}:${minute}`);
    this.updateTimes();
  }

  onRemoveTime(time: NgSelectOption) {
    this.selectTimesSet.delete(time.value);
    this.updateTimes();
  }

  onClearTimes() {
    this.selectTimesSet = new Set();
    this.updateTimes();
  }

  // Close on open - this prevents the user from using the times dropdown
  onOpenTimes() {
    this.timesDropdown.close();
  }

  // Convert set to array and update the create request
  updateTimes() {
    this.selectedTimes = Array.from(this.selectTimesSet);
    this.updateMAREditedAction(this.timesComponent.name, this.selectedTimes);
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
    this.orderCreateRequestEvent.emit({
      updates: {
        postActions: [this.MAREditedAction],
      },
    });
  }

  /****************************************************************************
   * Days of the Week Components                                              *
   ****************************************************************************/

  onDaysOfTheWeekClose() {
    this.daysOfTheWeekDropdown.close();
  }

  onSelectedDaysOfTheWeekChange() {
    this.sortDaysOfTheWeekIfNeeded();
    this.updateMAREditedAction(
      this.daysOfTheWeekComponent.name,
      this.selectedDaysOfTheWeek
    );
    this.setAreSelectedTimesOrSelectedDaysOfTheWeekPopulated();
    this.orderCreateRequestEvent.emit({
      updates: {
        postActions: [this.MAREditedAction],
      },
    });
  }

  // Sort by original array's order, if needed
  sortDaysOfTheWeekIfNeeded() {
    if (
      !this.selectedDaysOfTheWeek.every(
        (value, index, array) =>
          this.dayOfTheWeekSortValueByDay[value] >=
          this.dayOfTheWeekSortValueByDay[array[index - 1]]
      )
    ) {
      this.selectedDaysOfTheWeek = [
        ...this.selectedDaysOfTheWeek.sort(
          (a, b) =>
            this.dayOfTheWeekSortValueByDay[a] -
            this.dayOfTheWeekSortValueByDay[b]
        ),
      ];
    }
  }

  onClickDaysOfTheWeekSelectAll() {
    if (this.areAllDaysOfTheWeekSelected) {
      this.selectedDaysOfTheWeek = [];
    } else {
      this.selectedDaysOfTheWeek =
        this.daysOfTheWeekComponent.dropdownItems!.map(
          (dayOfTheWeek) => dayOfTheWeek.value!
        );
    }
    this.daysOfTheWeekDropdown.close();
    this.onSelectedDaysOfTheWeekChange();
  }

  get areAllDaysOfTheWeekSelected() {
    return (
      this.selectedDaysOfTheWeek.length ===
      this.daysOfTheWeekComponent.dropdownItems!.length
    );
  }

  // For checkboxes for individual days of the week
  isDayOfTheWeekSelected(dropdownItem: DropdownItem): boolean {
    return this.selectedDaysOfTheWeek.includes(dropdownItem.value);
  }

  /****************************************************************************
   * Frequency Component                                                      *
   ****************************************************************************/
  onAdministrationFrequencyChange() {
    this.selectedAdministrationFrequency = this.administrationFrequency;
    this.setIsAdministrationFrequencyPopulated();
    this.updateMAREditedAction(
      this.frequencyComponent.name,
      this.selectedAdministrationFrequency
    );
    this.orderCreateRequestEvent.emit({
      updates: {
        postActions: [this.MAREditedAction],
      },
    });
  }

  onClearAdministrationFrequency() {
    this.administrationFrequency = undefined;
    this.setIsAdministrationFrequencyPopulated();
  }

  /****************************************************************************
   * Notes Component                                                          *
   ****************************************************************************/

  onNotesChange(event: Event) {
    if (typeof event === "string") {
      this.updateMAREditedAction(this.notesComponent.name, event);
      this.orderCreateRequestEvent.emit({
        updates: {
          postActions: [this.MAREditedAction],
        },
      });
    }
  }

  updateMAREditedAction(field: string, value: string | string[]) {
    if (Array.isArray(value) && value.length === 0) {
      value = undefined;
    }
    this.MAREditedAction = {
      ...this.MAREditedAction,
      [field]: value,
    };

    this.MAREditedAction.reason = MAREditedAction.getMAREditedReason(
      this.MAREditedAction
    );
  }

  getBaseMAREditedAction(): MAREditedAction {
    return {
      actionType: Action.ActionTypeEnum.MAREdited,
    };
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/mar-details/mar-details.ts